export interface AibPaymentFormData {
  // TODO this does not exist in documentation
  // https://docs.fiserv.dev/public/docs/mandatory-fields
  // https://docs.fiserv.dev/public/docs/payments-hosted-form-fields
  assignToken: boolean;


  /**
   3-D Secure (https://docs.fiserv.dev/public/docs/payments-hosted-form-fields)
   Optional parameter to be set either to ‘true’ or ‘false’ to enable or disable 3-D Secure authentication on a transaction-by-transaction basis.

   Example for a transaction with 3-D Secure:
   <input type="hidden" name="authenticateTransaction" value="true"/>

   Example for a transaction without 3-D Secure:
   <input type="hidden" name="authenticateTransaction" value="false"/>
   */
  authenticateTransaction: boolean;

  /**
   Optional parameter for EMV 3-D Secure v2 to indicate the preferred type of authentication:

   01 = no preference (set as default value)
   02 = no challenge requested
   03 = challenge requested (3DS requestor preference)
   04 = challenge requested (mandate)
   05 = No challenge requested (Transaction Risk Analysis is already performed)
   06 = No challenge requested (Data Share Only)
   07 = No challenge requested (SCA is already performed)
   08 = No challenge requested (Utilize whitelist exemption if no challenge required)
   09 = Challenge requested (Whitelist prompt requested if challenge required)
   */
  threeDSRequestorChallengeIndicator: string; // we should use '04'

  /**
   This is the total amount of the transaction using a dot or comma as decimal separator,
   e. g. 12.34 for an amount of 12 Euro and 34 Cent. Group separators like 1,000.01 / 1.000,01 are not allowed.
   */
  chargetotal: string;

  /**
   Represents the numeric ISO code of the transaction currency, e.g. 978 for EUR.
   */
  currency: string;

  /**
   Set the value for this parameter to ‘combinedpage’ for a standard hosted payment page integration
   */
  checkoutoption: string;

  /**
   Validity Checks & Errors
   Additionally when using your own error page for negative validity checks (full_bypass=true):

   Field Name	-> Description
   fail_reason_details	->
   Comma separated list of missing or invalid variables.
   Note that fail_reason_details will not be supported in case of payplus and fullpay mode.
   invalid_cardholder_data ->
   true – if validation of card holder data was negative.
   false – if validation of card holder data was positive but
   transaction has been declined due to other reasons.
   */
  full_bypass: boolean;

  /**
   Order ID represents transaction identification number, must be unique for all primary transactions,
   if not provided in "sale", "preauth" or "payerAuth" request, the Gateway populates the parameter automatically.
   */
  oid?: string;

  /**
   Represents unique identification of your account in our Gateway system.
   */
  storename: string;

  /**
   Time zone of the transaction in Area/Location format, e.g.
   Africa/Johannesburg
   America/New_York
   America/Sao_Paulo
   Asia/Calcutta
   Australia/Sydney
   Europe/Amsterdam
   Europe/Berlin
   Europe/Dublin
   Europe/London
   */
  timezone: string;

  /** Data Vault Tokenisation ( https://docs.fiserv.dev/public/docs/data-vault-tokenisation-1 )
   Creating a token
   If you use a HTML form to initiate the payment, you can send the parameter hosteddataid together with the transaction data
   as a unique identification for the payment information in this transaction.

   Depending on the payment type, credit card number and expiry date or IBAN and account holder name will be stored under this ID
   if the transaction has been successful. In cases where the submitted hosteddataid already exists for your store, the stored payment
   information will be updated.

   If you want to assign multiple IDs to the same payment information record, you can submit the parameter hosteddataid several
   times with different values in the same transaction.

   If you prefer not to assign a token yourself but want to let the Gateway do this for you, send the parameter assignToken and
   set it to ‘true’. The Gateway will then assign a token and include it in the transaction response as hosteddataid.

   If you have use cases where you need some of the tokens for single transactions only (e.g. for consumers that check
   out as a “guest”, use the additional parameter tokenType with the values ‘ONETIME’ (card details will only be stored for a short period of time) or ‘MULTIPAY’ (card details will be stored for use in future transactions).
   */
  tokenType: string;
  hosteddataid?: string;

  /**
   The URL where you wish to direct customers after a successful transaction (your Thank You URL)
   */
  responseSuccessURL: string;

  /**
   The URL where you wish to direct customers after a declined or unsuccessful transaction (your Sorry URL)
   */
  responseFailURL: string;

  /**
   Server-to-server Notification ( https://docs.fiserv.dev/public/docs/response-fields#server-to-server-notification )
   Please note, that:
   • The transaction URL is sent as received therefore please don’t add additional escaping (e.g.: using %2f for a Slash(/).
   • No SSL handshake or verification of SSL certificates will be done in this process.
   • The notification URL needs to listen on port 443 (https) – other ports are not supported. ==> // TODO: I think this is outdated
   */
  transactionNotificationURL: string;

  /** Represents the origin of a transaction. Available values are:
   ‘MAIL’ - for transactions where the payment details are captured manually and provided in written form the Card Code entry is not allowed
   ‘PHONE’ - for transactions where you have received the order over the phone and enter the payment details yourself the Card Code entry is required
   ‘ECI‘ - for standard usage in an eCommerce environment where your customer enters the payment details
   */
  trxOrigin: string;

  /**
   Exact transaction date and time; in the format "YYYY:MM:DD-hh:mm:ss"
   */
  txndatetime: string;

  /**
   The field represents a transaction type. Available values:'sale’, 'preauth’, 'postauth’, or ‘payer_auth’
   */
  txntype: string;

  /**
   Credentials on file (COF) specific parameter to flag transactions as unscheduled credential on file type.
   Available values are:
   FIRST - for initial transaction in a series
   CARDHOLDER_INITIATED - for an unscheduled transaction initiated by your cardholder
   MERCHANT_INITIATED - for an unscheduled transaction initiated by yourselves
   */
  unscheduledCredentialOnFileType: string;

  /**
   This is to indicate the algorithm that you use for hash calculation.
   Available values are:
   • HMACSHA256
   • HMACSHA384
   • HMACSHA512
   Only one algorithm value should be used.
   */
  hash_algorithm: 'HMACSHA256' | 'SHA256' | 'HMACSHA384' | 'HMACSHA512';

  /** The extended hash to be calculated using all non-empty Gateway specified request parameters in ascending order
   of the parameter names, where the upper-case characters come before the lower case (based on ASCII value)
   and the shared secret must be used as the secret key for calculating the hash value.
   */
  hashExtended?: string;

  /**
   Represents Value Added Tax or other taxes, e.g.: GST in Australia.
   Sub total amount + shipping + tax = charge total
   */
  vattax?: number;

  /**
   Optional parameter to be submitted if needed for your future reference; can be used as a reference for subsequent transactions.
   */
  merchantTransactionId?: string;

  /**
   we use the hosted page so the following are not needed
   */

  /**
   * If you prefer to select the payment method yourself,
   * submit this parameter along with your 'Sale' or 'PreAuth' transaction.
   * If you do not submit this parameter, the Gateway displays a drop-down menu to the customer
   * to choose from the payment methods available for your shop.
   */
  paymentMethod?: string; // Any of: 'paypal', 'applePay', 'M'/ Mastercard , 'V' / Visa

  /**
   * Field we use to track from which application the transaction was made from
   */
  customParam_source?: string;

  /**
   * https://docs.fiserv.dev/public/docs/mandatory-fields#optional-fields
   * If you plan to embed our hosted payment page inside an iFrame you must use this parameter,
   * with the maximum length of 2048 characters, to specify an URL of a page, where the hosted payment page will be embedded.
   * However, note that we do not recommend using the hosted payment forms inside an iFrame since
   * some Internet browsers do not allow cookies to be sent to the 3rd party hosts, moreover some
   * features (e.g.: 3-D Secure authentications) and some Alternative Payment methods that involve
   * redirections to the 3rd party services (e.g.: iDEAL or PayPal) do not allow displaying their screens within an iFrame.
   */
  parentUri?: string;

}

/**
 * Documentation can be found here: https://docs.fiserv.dev/public/docs/response-fields
 */
export interface AibPaymentResponse {

  /**
   Approval code for the transaction.
   The first character of this parameter is the most helpful indicator for verification of the transaction result.

   Y indicates that the transaction has been successful,
   N indicates that the transaction has not been successful,
   ? indicates that the transaction has been successfully initialized,
   but a final result is not yet available since the transaction is now in a waiting status.
   The transaction status will be updated at a later stage.
   */
  approval_code: string;

  /**
   Order ID, if not defined in the request, the Gateway generates it automatically
   e.g. C-2101f68a-45e9-4f3c-a6da-1337d5574717
   */
  oid: string;

  /**
   Reference number
   */
  refnumber: string;

  /**
   Transaction status

   APPROVED, DECLINED (by authorisation endpoint or due to fraud prevention settings),
   FAILED (wrong transaction message content/parameters, etc.)
   or WAITING (asynchronous Alternative Payment Methods)
   */
  status: 'APPROVED' | 'DECLINED' | 'FAILED' | 'WAITING';

  /**
   Time of transaction processing
   */
  txndate_processed: string;
  /**
   Transaction identifier assigned by the Gateway, e.g. to be used for a Void
   */
  ipgTransactionId: string;

  /**
   Identification for the specific transaction
   */
  tdate: string;

  /**
   Reason the transaction failed
   */
  fail_reason: string;

  /**
   Hash-Value to protect the communication See note below (link is invalid)
   */
  response_hash: string;

  /**
   Hash-Value to protect the communication, where all response parameters are included in the hash calculation see note below.
   */
  extended_response_hash: string;

  /**
   The response code provided by the backend system.

   Note that response codes can be different depending on the used payment type and backend system.
   While for credit card payments, the response code 00 is the most common response for an approval,
   the backend for giropay transactions for example returns the response code 4000 for successful transactions.
   */
  processor_response_code: string;

  /**
   Internal processing code for failed transactions
   */
  fail_rc: string;

  /**
   Terminal ID used for transaction processing
   */
  terminal_id: string;

  /**
   6 digit identifier of the card issuing bank
   */
  ccbin: string;

  /**
   3 letter alphanumeric ISO code of the cardholder’s country

   e.g. USA, DEU, ITA, etc. Filled with N/A if the cardholder’s country cannot be determined or the payment type is not credit card
   */
  cccountry: string;

  /**
   Brand of the credit or debit card

   MASTERCARD, VISA, AMEX, DINERSCLUB, JCB, CUP, CABAL, MAESTRO, RUPAY, BCMC, SOROCRED. Filled with N/A for any payment method which is not a credit card or debit card.
   */
  ccbrand: string;

  /**
   Used in the authorization response, for both approved and declined transactions, indicating when consumer non-reloadable
   prepaid cards and single-use VCNs are recognized.
   */
  merchantAdviceMessage: string
}